import React, { useEffect, useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import DataHandler from "../../handlers/DataHandler";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const emptyEditor = () => EditorState.createEmpty();

function editorFromContent(content) {
  try {
    if (!content) return emptyEditor();

    const parsed = JSON.parse(content);
    if (!parsed?.[0]) return emptyEditor();

    return EditorState.createWithContent(convertFromRaw(parsed[0]));
  } catch {
    return emptyEditor();
  }
}

export default function PassageModal({
  show,
  mode,
  passage,
  passageTypeId,
  onClose,
  onSubmit,
}) {
  const isEdit = mode === "edit";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentEditorState, setContentEditorState] = useState(emptyEditor());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;

    if (isEdit && passage) {
      setTitle(passage.title || "");
      setDescription(passage.description || "");
      setContentEditorState(editorFromContent(passage.content));
    } else {
      setTitle("");
      setDescription("");
      setContentEditorState(emptyEditor());
    }
  }, [show, isEdit, passage]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      if (window.Swal) {
        window.Swal.fire({
          title: "Failed!",
          text: "Passage title is required",
          icon: "error",
        });
      }
      return;
    }

    if (!description.trim()) {
      if (window.Swal) {
        window.Swal.fire({
          title: "Failed!",
          text: "Passage description is required",
          icon: "error",
        });
      }
      return;
    }

    if (!contentEditorState.getCurrentContent().hasText()) {
      if (window.Swal) {
        window.Swal.fire({
          title: "Failed!",
          text: "Passage content is required",
          icon: "error",
        });
      }
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      content: JSON.stringify([
        convertToRaw(contentEditorState.getCurrentContent()),
      ]),
      passage_type_id: passageTypeId,
      created_by: DataHandler.getFromSession("manage_user_id"),
    };

    try {
      setSaving(true);
      await onSubmit(payload);

      if (window.Swal) {
        window.Swal.fire({
          title: "Success!",
          text: isEdit ? "Passage updated successfully" : "Passage added successfully",
          icon: "success",
        });
      }

      onClose();
    } catch (error) {
      if (window.Swal) {
        window.Swal.fire({
          title: "Failed!",
          text: error?.message || "Unable to save passage",
          icon: "error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit Passage" : "Create New Passage"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} md="9">
              <Form.Label>Title*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={30}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12">
              <Form.Label>Content*</Form.Label>
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: 4,
                  padding: 10,
                  backgroundColor: "#fff",
                }}
              >
                <Editor
                  editorState={contentEditorState}
                  onEditorStateChange={setContentEditorState}
                  toolbar={{
                    options: [
                      "inline",
                      "blockType",
                      "fontFamily",
                      "list",
                      "textAlign",
                      "link",
                      "image",
                      "history",
                    ],
                    blockType: { inDropdown: true },
                    fontFamily: { inDropdown: true },
                    list: { inDropdown: true },
                    textAlign: { inDropdown: true },
                  }}
                />
              </div>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="9">
              <Form.Label>Description*</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Type description here..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={120}
              />
            </Form.Group>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving..." : isEdit ? "Edit" : "Save"}
        </Button>

        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}